import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { IconArrowBackUp, IconLoader2 } from '@tabler/icons-react'
import { useUnsavedChanges } from '../lib/unsavedChanges'
import { useI18n } from '../lib/i18n'

/** El hueco de guardar y descartar: en medio de la barra, tras el contexto. */
export const DASHBOARD_TOPBAR_ACTIONS_ID = 'dashboard-topbar-pending-actions'
/** El hueco del final de la barra, tras la campana: el menú de usuario. */
export const DASHBOARD_TOPBAR_END_ID = 'dashboard-topbar-end'

function useSlot(id: string) {
  const [target, setTarget] = useState<HTMLElement | null>(null)
  useEffect(() => { setTarget(document.getElementById(id)) }, [id])
  return target
}

/**
 * Guardar y descartar en la barra, por un portal a su hueco de en medio.
 * Cada form sigue mandando sobre lo suyo; aquí sólo se decide dónde se ve.
 */
export function DashboardTopbarActions({ children }: { children: ReactNode }) {
  const target = useSlot(DASHBOARD_TOPBAR_ACTIONS_ID)
  return target ? createPortal(children, target) : null
}

/**
 * Lo que va al final de la barra, pegado a la derecha tras la campana: el menú
 * de usuario en las pantallas que no tienen menú lateral donde ponerlo.
 *
 * Es otro hueco que el de guardar y descartar, a propósito: ése se centra entre
 * el contexto y el buscador, y el menú de usuario metido ahí quedaba en medio
 * de la barra en vez de en su esquina.
 */
export function DashboardTopbarEnd({ children }: { children: ReactNode }) {
  const target = useSlot(DASHBOARD_TOPBAR_END_ID)
  return target ? createPortal(children, target) : null
}

/**
 * Guardar y descartar, siempre en el mismo sitio.
 *
 * Aparece sólo cuando hay algo que guardar, y siempre arriba a la derecha, al
 * lado de las notificaciones. Antes cada pantalla lo resolvía por su cuenta
 * —una barra pegada abajo, un botón al pie de una tarjeta, otro dentro del
 * formulario— y había que buscar dónde estaba el guardar de cada una. Estando
 * siempre en el mismo píxel, su sola aparición ya significa «tienes cambios».
 *
 * Cada pantalla sigue mandando sobre lo suyo: qué es estar sucio, qué se manda
 * y qué se descarta. Aquí sólo vive la presentación, que es lo que tenía que
 * dejar de repetirse.
 *
 * Y de paso apunta la pantalla en el registro de cambios sin guardar: quien
 * pone esta barra queda protegido contra irse a medias sin tener que cablear
 * nada. Antes eso se hacía a mano pantalla por pantalla, y las nuevas se
 * olvidaban.
 */
export function PendingChangesActions({
  dirty,
  saving = false,
  disabled = false,
  attentionKey = 0,
  savingLabel,
  onDiscard,
  onSave,
}: {
  /** Si no hay cambios no hay barra: su presencia es el aviso. */
  dirty: boolean
  saving?: boolean
  /** Otras razones para no dejar tocar los botones (una subida en curso, por ejemplo). */
  disabled?: boolean
  /**
   * Cambiarlo remonta la barra y dispara su animación de aviso. Sirve para
   * cuando alguien intenta salir con cambios sin guardar: la barra ya estaba
   * ahí, y sin esto no habría nada que mirar.
   */
  attentionKey?: number
  /** Por defecto, «Guardando…». Se resuelve en el cuerpo: aquí todavía no hay traductor. */
  savingLabel?: string
  onDiscard: () => void
  onSave: () => void
}) {
  /* Los avisos de fuera —el taller pidiendo atención— y los de dentro se suman:
     da igual quién llame, la barra parpadea una vez por llamada. */
  const [flashes, setFlashes] = useState(0)
  useUnsavedChanges(dirty, () => setFlashes((current) => current + 1))
  const attention = attentionKey + flashes

  if (!dirty) return null

  return (
    <DashboardTopbarActions>
      <PendingChangesBar
        attention={attention}
        saving={saving}
        disabled={disabled}
        {...(savingLabel ? { savingLabel } : {})}
        onDiscard={onDiscard}
        onSave={onSave}
      />
    </DashboardTopbarActions>
  )
}

/**
 * La cápsula de cambios sin guardar, sola.
 *
 * Es lo que se ve: el aviso, «Descartar» y «Guardar cambios». La barra del panel
 * la monta por un portal (`PendingChangesActions`) y los asistentes la ponen en
 * su propia cabecera, con sus palabras —«Cancelar», «Guardar cambios»—: que sea
 * la misma pieza es lo que hace que el aviso se lea igual en todas partes, con
 * la misma entrada y la misma sacudida.
 */
export function PendingChangesBar({
  saving = false,
  disabled = false,
  attention = 0,
  discardLabel,
  saveLabel,
  savingLabel,
  onDiscard,
  onSave,
}: {
  saving?: boolean
  disabled?: boolean
  /** Cambiarlo vuelve a montar la cápsula con su sacudida de aviso. */
  attention?: number
  /** Por defecto, «Descartar». Un asistente dice «Cancelar». */
  discardLabel?: string
  /** Por defecto, «Guardar cambios» (y «Guardar» en un teléfono estrecho). */
  saveLabel?: string
  /** Por defecto, «Guardando…». */
  savingLabel?: string
  onDiscard: () => void
  onSave: () => void
}) {
  const { t } = useI18n()
  const discard = discardLabel ?? t('common.discard')

  return (
    <div
      key={attention}
      className={`dash-topbar-pending-actions${attention > 0 ? ' is-attention' : ''}`}
      role="status"
      aria-live="polite"
    >
      {/* Lo que dice la barra de Shopify cuando hay algo sin guardar: no sólo
          dos botones, también QUÉ pasa. El punto de la marca es el aviso; el
          texto se esconde en el teléfono, donde no hay sitio y los botones
          bastan. */}
      <span className="dash-topbar-pending-label">
        <span className="dash-topbar-pending-dot" aria-hidden="true" />
        {t('topbar.unsavedChanges')}
      </span>
      <button
        className="dash-secondary-btn"
        type="button"
        disabled={saving || disabled}
        aria-label={discard}
        onClick={onDiscard}
      >
        {/* En el teléfono «Descartar» pasa a icono para que «Guardar cambios»
            entre entero: ver `dash-discard-icon` en la hoja de estilos. */}
        <IconArrowBackUp className="dash-discard-icon" size={15} aria-hidden="true" />
        <span className="dash-discard-text">{discard}</span>
      </button>
      <button
        className="dash-primary-btn dash-new-project-btn"
        type="button"
        disabled={saving || disabled}
        onClick={onSave}
      >
        {saving ? <IconLoader2 className="dash-spin" size={14} /> : null}
        {saving ? (savingLabel ?? t('common.saving')) : saveLabel ? (
          <span className="dash-btn-label">{saveLabel}</span>
        ) : (
          <>
            <span className="dash-btn-label dash-btn-label--desktop">{t('topbar.saveChanges')}</span>
            <span className="dash-btn-label dash-btn-label--compact">{t('common.save')}</span>
          </>
        )}
      </button>
    </div>
  )
}
